import { format } from 'date-fns';
import { AnalyticsDate, AnalyticsId } from './Analytics';

export interface ButtonClickMetadata {
	type: string;
	text: string;
	id?: string;
	locationId: string;
	exhibitId: string;
	platform: string;
	browser: string;
	appSession: string;
}

export interface ButtonClickResponse {
	_id: AnalyticsId;
	type: string;
	udid: string;
	os: string;
	metadata: ButtonClickMetadata;
	ip: string;
	createdDate: AnalyticsDate;
	modifiedDate: AnalyticsDate;
	status: number;
}

export interface ButtonClickData {
	topicId: string;
	buttonId: string;
	buttonName: string;
	clicks: UserTraffic[];
}

export interface UserTraffic {
	key: string;
	value: number;
	display: string;
	timestamp: number;
}

/**
 * Parses an AnalyticsDate into a JavaScript Date object
 * @param date - The AnalyticsDate to parse
 * @returns JavaScript Date object
 * @throws Error if date cannot be parsed
 */
const parseAnalyticsDate = (date: AnalyticsDate): Date => {
	if (typeof date === 'string') {
		const parsed = new Date(date);
		if (isNaN(parsed.getTime())) {
			throw new Error(`Invalid date string: ${date}`);
		}
		return parsed;
	}

	if (typeof date === 'object' && date.$date?.$numberLong) {
		const timestamp = parseInt(date.$date.$numberLong);
		if (isNaN(timestamp)) {
			throw new Error(`Invalid timestamp: ${date.$date.$numberLong}`);
		}
		return new Date(timestamp);
	}

	throw new Error('Invalid date format');
};

/**
 * This function aggregates button clicks from the raw data by day.
 * Uses metadata.id for identification, with backward compatibility for metadata.text and metadata.exhibitId
 *
 * @param rawData - ButtonClickResponse[] data from the API
 * @param locationId - location ID to filter by
 * @returns an array of aggregated button clicks by day
 */
export const aggregateButtonClicks = (rawData: ButtonClickResponse[], locationId: string): ButtonClickData[] => {
	if (!rawData || !Array.isArray(rawData)) {
		return [];
	}

	if (!locationId) {
		throw new Error('locationId is required');
	}

	// Create a Map to organize buttons by buttonId
	const buttonClicksMap: { [buttonId: string]: { buttonName: string; clicks: UserTraffic[]; topicId: string } } = {};

	// Loop through each button click in the raw data
	rawData.forEach((clickData) => {
		// Filter by location
		if (clickData.metadata.locationId !== locationId) {
			return;
		}

		// Validate required fields
		if (!clickData.metadata.text || !clickData.metadata.exhibitId) {
			console.warn('Skipping click with missing required metadata:', clickData);
			return;
		}

		// Determine button identifier - use metadata.id if available, otherwise fall back to text + exhibitId
		let buttonId: string;
		let buttonName: string;

		if (clickData.metadata.id) {
			buttonId = clickData.metadata.id;
			buttonName = clickData.metadata.text;
		} else {
			// Backward compatibility: use text + exhibitId as identifier
			buttonId = `${clickData.metadata.text}_${clickData.metadata.exhibitId}`;
			buttonName = clickData.metadata.text;
		}

		// Parse the created date with error handling
		let date: Date;
		try {
			date = parseAnalyticsDate(clickData.createdDate);
		} catch (error) {
			console.warn('Skipping click with invalid date:', clickData, error);
			return;
		}

		// Set the key and display for this button click
		const dateAsKey = format(date, 'M/d');
		const display = format(date, 'MMM d, yyyy');

		// Initialize the button map if undefined
		if (!buttonClicksMap[buttonId]) {
			buttonClicksMap[buttonId] = {
				topicId: clickData.metadata.exhibitId,
				buttonName,
				clicks: [],
			};
		}

		// Look for an existing button click with the same key
		const existingItem = buttonClicksMap[buttonId].clicks.find((click) => click.key === dateAsKey);

		// If a button click with the same date/key exists
		if (existingItem) {
			// Increment its value by 1
			existingItem.value += 1;
		} else {
			// Otherwise, add a new button click with a value of 1
			buttonClicksMap[buttonId].clicks.push({
				key: dateAsKey,
				value: 1,
				display,
				timestamp: date.getTime(),
			});
		}
	});

	// Convert the map to the final array format and sort
	return Object.keys(buttonClicksMap).map((buttonId) => ({
		topicId: buttonClicksMap[buttonId].topicId,
		buttonId,
		buttonName: buttonClicksMap[buttonId].buttonName,
		clicks: buttonClicksMap[buttonId].clicks.sort((a, b) => a.timestamp - b.timestamp),
	}));
};
